// src/components/organization/BillingSection.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
    CreditCardIcon, PlusIcon, TrashIcon, CheckCircleIcon,
    ShieldCheckIcon, BanknotesIcon, DevicePhoneMobileIcon,
    DocumentTextIcon, ArrowDownTrayIcon, XMarkIcon, StarIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

// ==================== HELPERS ====================
const formatCurrency = (amount, currency = 'EUR') => {
    if (!amount) return '—';
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency }).format(amount);
};

const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
};

// ==================== CARD BRAND ====================
const CardBrand = ({ brand }) => {
    const brands = {
        visa: { label: 'VISA', bg: 'bg-blue-700', style: 'font-black italic text-xs' },
        mastercard: { label: 'MC', bg: 'bg-red-600', style: 'font-bold text-xs' },
        amex: { label: 'AMEX', bg: 'bg-blue-900', style: 'font-bold text-[10px]' },
    };
    const b = brands[brand?.toLowerCase()] || { label: '💳', bg: 'bg-gray-500', style: 'text-sm' };
    return (
        <div className={`w-11 h-7 ${b.bg} rounded-md flex items-center justify-center flex-shrink-0`}>
            <span className={`text-white ${b.style}`}>{b.label}</span>
        </div>
    );
};

const MobileBrand = ({ provider }) => {
    const icons = { WAVE: '🌊', ORANGE_MONEY: '🟠', MTN_MONEY: '🟡' };
    return (
        <div className="w-11 h-7 bg-emerald-100 rounded-md flex items-center justify-center text-base flex-shrink-0">
            {icons[provider] || '📱'}
        </div>
    );
};

// ==================== MODAL AGGIUNGI METODO ====================
const AddPaymentModal = ({ onClose, onAdd, region }) => {
    const { t } = useTranslation();
    const [tab, setTab] = useState(region === 'WEST_AFRICA' ? 'mobile' : 'card');
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const [card, setCard] = useState({ name: '', number: '', expiry: '', cvc: '' });
    const [mobile, setMobile] = useState({ provider: 'WAVE', phone: '' });

    const fmtNumber = (v) => v.replace(/\D/g, '').slice(0, 16).replace(/(.{4})/g, '$1 ').trim();
    const fmtExpiry = (v) => {
        const c = v.replace(/\D/g, '').slice(0, 4);
        return c.length >= 3 ? c.slice(0, 2) + '/' + c.slice(2) : c;
    };

    const validate = () => {
        const e = {};
        if (tab === 'card') {
            if (!card.name.trim()) e.name = 'Nome richiesto';
            if (card.number.replace(/\s/g, '').length < 16) e.number = 'Numero carta non valido';
            if (!card.expiry.match(/^\d{2}\/\d{2}$/)) e.expiry = 'Formato MM/AA';
            if (card.cvc.length < 3) e.cvc = 'CVC non valido';
        } else {
            if (!mobile.phone.trim()) e.phone = 'Numero richiesto';
            if (!/^\+?[0-9\s]{8,15}$/.test(mobile.phone)) e.phone = 'Numero non valido';
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setLoading(true);
        await new Promise(r => setTimeout(r, 1000)); // simula chiamata API
        const newMethod = tab === 'card'
            ? { id: Date.now().toString(), type: 'card', brand: 'visa', last4: card.number.replace(/\s/g, '').slice(-4), expiry: card.expiry, name: card.name, isDefault: false }
            : { id: Date.now().toString(), type: 'mobile', provider: mobile.provider, phone: mobile.phone, isDefault: false };
        setLoading(false);
        onAdd(newMethod);
        toast.success(tab === 'card' ? 'Carta aggiunta!' : 'Mobile Money salvato!');
    };

    const ic = (err) => `w-full px-3 py-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white ${err ? 'border-red-400 bg-red-50' : 'border-gray-200'}`;

    const PROVIDERS = [
        { id: 'WAVE', icon: '🌊', label: 'Wave Mobile Money' },
        { id: 'ORANGE_MONEY', icon: '🟠', label: 'Orange Money' },
        { id: 'MTN_MONEY', icon: '🟡', label: 'MTN Mobile Money' },
    ];

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md">

                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 rounded-t-2xl flex items-center justify-between">
                    <h3 className="font-bold text-white">Aggiungi metodo di pagamento</h3>
                    <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-lg transition">
                        <XMarkIcon className="w-5 h-5 text-white" />
                    </button>
                </div>

                <div className="p-6 space-y-4">
                    {/* Toggle tipo */}
                    <div className="grid grid-cols-2 gap-2 p-1 bg-gray-100 rounded-xl">
                        {[
                            { id: 'card', icon: <CreditCardIcon className="w-4 h-4" />, label: 'Carta' },
                            { id: 'mobile', icon: <DevicePhoneMobileIcon className="w-4 h-4" />, label: 'Mobile Money' },
                        ].map(t => (
                            <button key={t.id} onClick={() => { setTab(t.id); setErrors({}); }}
                                className={`flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition ${tab === t.id ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500'}`}>
                                {t.icon}{t.label}
                            </button>
                        ))}
                    </div>

                    {/* Form carta */}
                    {tab === 'card' && (
                        <div className="space-y-3">
                            <div>
                                <label className="text-xs font-medium text-gray-600 block mb-1">Titolare *</label>
                                <input type="text" value={card.name} onChange={e => { setCard({ ...card, name: e.target.value }); setErrors({ ...errors, name: '' }); }}
                                    className={ic(errors.name)} placeholder="Nome Cognome" />
                                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                            </div>
                            <div>
                                <label className="text-xs font-medium text-gray-600 block mb-1">Numero carta *</label>
                                <div className="relative">
                                    <input type="text" value={card.number}
                                        onChange={e => { setCard({ ...card, number: fmtNumber(e.target.value) }); setErrors({ ...errors, number: '' }); }}
                                        className={`${ic(errors.number)} pr-24 font-mono`} placeholder="1234 5678 9012 3456" maxLength={19} />
                                    <div className="absolute right-3 top-1/2 -translate-y-1/2 flex gap-1">
                                        <CardBrand brand="visa" /><CardBrand brand="mastercard" />
                                    </div>
                                </div>
                                {errors.number && <p className="text-red-500 text-xs mt-1">{errors.number}</p>}
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs font-medium text-gray-600 block mb-1">Scadenza *</label>
                                    <input type="text" value={card.expiry}
                                        onChange={e => { setCard({ ...card, expiry: fmtExpiry(e.target.value) }); setErrors({ ...errors, expiry: '' }); }}
                                        className={`${ic(errors.expiry)} font-mono`} placeholder="MM/AA" maxLength={5} />
                                    {errors.expiry && <p className="text-red-500 text-xs mt-1">{errors.expiry}</p>}
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-gray-600 block mb-1">CVC *</label>
                                    <input type="password" value={card.cvc}
                                        onChange={e => { setCard({ ...card, cvc: e.target.value.replace(/\D/g, '').slice(0, 4) }); setErrors({ ...errors, cvc: '' }); }}
                                        className={`${ic(errors.cvc)} font-mono`} placeholder="•••" maxLength={4} />
                                    {errors.cvc && <p className="text-red-500 text-xs mt-1">{errors.cvc}</p>}
                                </div>
                            </div>
                            <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-xl">
                                <ShieldCheckIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                                <p className="text-xs text-emerald-700">Pagamento sicuro via Stripe — dati non memorizzati</p>
                            </div>
                        </div>
                    )}

                    {/* Form Mobile Money */}
                    {tab === 'mobile' && (
                        <div className="space-y-3">
                            <div>
                                <label className="text-xs font-medium text-gray-600 block mb-2">Provider *</label>
                                <div className="space-y-2">
                                    {PROVIDERS.map(p => (
                                        <button key={p.id} onClick={() => setMobile({ ...mobile, provider: p.id })}
                                            className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 text-left transition ${mobile.provider === p.id ? 'border-blue-500 bg-blue-50' : 'border-gray-100 hover:bg-gray-50'}`}>
                                            <span className="text-xl">{p.icon}</span>
                                            <span className={`text-sm font-medium flex-1 ${mobile.provider === p.id ? 'text-blue-700' : 'text-gray-700'}`}>{p.label}</span>
                                            {mobile.provider === p.id && <CheckCircleIcon className="w-4 h-4 text-blue-500" />}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-medium text-gray-600 block mb-1">Numero di telefono *</label>
                                <div className="relative">
                                    <DevicePhoneMobileIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input type="tel" value={mobile.phone}
                                        onChange={e => { setMobile({ ...mobile, phone: e.target.value }); setErrors({ ...errors, phone: '' }); }}
                                        className={`${ic(errors.phone)} pl-9`} placeholder="+224 6XX XXX XXX" />
                                </div>
                                {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                                <p className="text-xs text-gray-400 mt-1">Riceverai una notifica push per confermare ogni pagamento</p>
                            </div>
                        </div>
                    )}

                    {/* Bottoni */}
                    <div className="flex gap-3 pt-2">
                        <button onClick={onClose} className="flex-1 py-2.5 border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition">
                            Annulla
                        </button>
                        <button onClick={handleSubmit} disabled={loading}
                            className="flex-1 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition">
                            {loading ? (
                                <span className="flex items-center justify-center gap-2">
                                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Salvataggio...
                                </span>
                            ) : 'Aggiungi'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ==================== BILLING SECTION ====================
const BillingSection = () => {
    const { organization, subscription, isSuperAdmin } = useAuth();
    const [showModal, setShowModal] = useState(false);
    const [methods, setMethods] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const currency = organization?.currency || 'EUR';
    const region = organization?.country === 'GN' || organization?.country === 'SN' || organization?.country === 'CI' || organization?.country === 'ML'
        ? 'WEST_AFRICA' : 'EUROPE';

    const handleAdd = (newMethod) => {
        setMethods(prev => {
            const updated = [...prev, newMethod];
            if (updated.length === 1) updated[0].isDefault = true;
            return updated;
        });
        setShowModal(false);
    };

    const handleRemove = (id) => {
        setMethods(prev => {
            const updated = prev.filter(m => m.id !== id);
            if (updated.length > 0 && !updated.find(m => m.isDefault)) updated[0].isDefault = true;
            return updated;
        });
        toast.success('Metodo rimosso');
    };

    const handleSetDefault = (id) => {
        setMethods(prev => prev.map(m => ({ ...m, isDefault: m.id === id })));
        toast.success('Metodo predefinito aggiornato');
    };

    return (
        <div className="space-y-5">

            {/* Banner SuperAdmin */}
            {isSuperAdmin && (
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-100 rounded-2xl p-4 flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center text-xl">👑</div>
                    <div>
                        <p className="text-sm font-bold text-purple-900">Account SuperAdmin</p>
                        <p className="text-xs text-purple-600 mt-0.5">Piano ENTERPRISE permanente — nessuna fatturazione richiesta</p>
                    </div>
                </div>
            )}

            {/* Piano corrente */}
            <div className="bg-white rounded-2xl border overflow-hidden">
                <div className="px-6 py-4 bg-gray-50 border-b flex items-center gap-2">
                    <StarIcon className="w-5 h-5 text-gray-400" />
                    <h3 className="font-semibold text-gray-900">Piano Corrente</h3>
                </div>
                <div className="p-6 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className={`px-4 py-2 rounded-xl text-sm font-bold ${subscription?.plan === 'ENTERPRISE' ? 'bg-purple-100 text-purple-800' :
                                subscription?.plan === 'PRO' ? 'bg-blue-100 text-blue-800' :
                                    'bg-gray-100 text-gray-700'
                            }`}>
                            {subscription?.plan || organization?.plan || 'FREE'}
                        </div>
                        <div>
                            <p className="text-sm text-gray-600">
                                {subscription?.status === 'ACTIVE' ? '✅ Attivo' : subscription?.status || 'Attivo'}
                            </p>
                            {subscription?.currentPeriodEnd && (
                                <p className="text-xs text-gray-400 mt-0.5">
                                    Rinnovo: {formatDate(subscription.currentPeriodEnd)}
                                </p>
                            )}
                        </div>
                    </div>
                    {!isSuperAdmin && subscription?.plan !== 'ENTERPRISE' && (
                        <a href="/settings?tab=plan"
                            className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition">
                            Aggiorna piano
                        </a>
                    )}
                </div>
            </div>

            {/* Metodi di pagamento */}
            <div className="bg-white rounded-2xl border overflow-hidden">
                <div className="px-6 py-4 bg-gray-50 border-b flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <CreditCardIcon className="w-5 h-5 text-gray-400" />
                        <h3 className="font-semibold text-gray-900">Metodi di Pagamento</h3>
                    </div>
                    <button onClick={() => setShowModal(true)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition">
                        <PlusIcon className="w-4 h-4" />
                        Aggiungi
                    </button>
                </div>

                <div className="p-6">
                    {methods.length === 0 ? (
                        <div onClick={() => setShowModal(true)}
                            className="border-2 border-dashed border-gray-200 rounded-xl p-10 text-center cursor-pointer hover:border-blue-300 hover:bg-blue-50/30 transition group">
                            <CreditCardIcon className="w-10 h-10 text-gray-300 group-hover:text-blue-400 mx-auto mb-3 transition" />
                            <p className="text-sm font-medium text-gray-500 group-hover:text-blue-600">Nessun metodo configurato</p>
                            <p className="text-xs text-gray-400 mt-1">Clicca per aggiungere carta o Mobile Money</p>
                            <div className="flex justify-center gap-2 mt-4 flex-wrap">
                                {region === 'WEST_AFRICA' ? (
                                    <>
                                        <span className="px-2.5 py-1 bg-gray-100 rounded-lg text-xs text-gray-500">🌊 Wave</span>
                                        <span className="px-2.5 py-1 bg-gray-100 rounded-lg text-xs text-gray-500">🟠 Orange Money</span>
                                        <span className="px-2.5 py-1 bg-gray-100 rounded-lg text-xs text-gray-500">🟡 MTN Money</span>
                                        <span className="px-2.5 py-1 bg-gray-100 rounded-lg text-xs text-gray-500">💳 Carta</span>
                                    </>
                                ) : (
                                    <>
                                        <span className="px-2.5 py-1 bg-gray-100 rounded-lg text-xs text-gray-500">💳 Visa</span>
                                        <span className="px-2.5 py-1 bg-gray-100 rounded-lg text-xs text-gray-500">💳 Mastercard</span>
                                        <span className="px-2.5 py-1 bg-gray-100 rounded-lg text-xs text-gray-500">💳 Amex</span>
                                    </>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {methods.map(m => (
                                <div key={m.id} className={`flex items-center gap-4 p-4 rounded-xl border-2 transition ${m.isDefault ? 'border-blue-200 bg-blue-50' : 'border-gray-100 hover:border-gray-200'}`}>
                                    {m.type === 'card' ? <CardBrand brand={m.brand} /> : <MobileBrand provider={m.provider} />}
                                    <div className="flex-1 min-w-0">
                                        {m.type === 'card' ? (
                                            <>
                                                <p className="text-sm font-semibold text-gray-900 font-mono">•••• •••• •••• {m.last4}</p>
                                                <p className="text-xs text-gray-400 mt-0.5">{m.name} · Scade {m.expiry}</p>
                                            </>
                                        ) : (
                                            <>
                                                <p className="text-sm font-semibold text-gray-900">{m.phone}</p>
                                                <p className="text-xs text-gray-400 mt-0.5">{m.provider.replace('_', ' ')}</p>
                                            </>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        {m.isDefault ? (
                                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">Predefinito</span>
                                        ) : (
                                            <button onClick={() => handleSetDefault(m.id)} className="text-xs text-blue-600 hover:underline whitespace-nowrap">
                                                Imposta default
                                            </button>
                                        )}
                                        <button onClick={() => handleRemove(m.id)}
                                            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition">
                                            <TrashIcon className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Storico fatture */}
            <div className="bg-white rounded-2xl border overflow-hidden">
                <div className="px-6 py-4 bg-gray-50 border-b flex items-center gap-2">
                    <DocumentTextIcon className="w-5 h-5 text-gray-400" />
                    <h3 className="font-semibold text-gray-900">Storico Fatture</h3>
                </div>
                {invoices.length === 0 ? (
                    <div className="p-12 text-center">
                        <p className="text-4xl mb-3">🧾</p>
                        <p className="text-sm font-medium text-gray-500">Nessuna fattura disponibile</p>
                        <p className="text-xs text-gray-400 mt-1">Le fatture appariranno qui dopo il primo pagamento</p>
                    </div>
                ) : (
                    <div className="divide-y">
                        {invoices.map((inv, i) => (
                            <div key={i} className="px-6 py-4 flex items-center gap-4 hover:bg-gray-50">
                                <DocumentTextIcon className="w-8 h-8 text-blue-400 flex-shrink-0" />
                                <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-900">Fattura #{inv.number}</p>
                                    <p className="text-xs text-gray-400">{formatDate(inv.date)}</p>
                                </div>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${inv.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-yellow-100 text-yellow-700'
                                    }`}>
                                    {inv.status === 'paid' ? '✓ Pagata' : 'In attesa'}
                                </span>
                                <p className="text-sm font-bold text-gray-900">{formatCurrency(inv.amount, currency)}</p>
                                {inv.pdfUrl && (
                                    <a href={inv.pdfUrl} target="_blank" rel="noreferrer"
                                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition">
                                        <ArrowDownTrayIcon className="w-4 h-4" />
                                    </a>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* GDPR */}
            <div className="bg-gray-50 border rounded-2xl p-4 flex items-start gap-3">
                <ShieldCheckIcon className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                <div>
                    <p className="text-sm font-semibold text-gray-700">Dati sicuri e conformi GDPR</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                        I dati di pagamento sono protetti con crittografia end-to-end. RoadCtrl non memorizza mai i dati della carta.
                        {region === 'WEST_AFRICA' && ' I pagamenti Mobile Money sono gestiti direttamente dai provider certificati.'}
                    </p>
                </div>
            </div>

            {showModal && <AddPaymentModal onClose={() => setShowModal(false)} onAdd={handleAdd} region={region} />}
        </div>
    );
};

export default BillingSection;